import React from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { useDispatch } from 'react-redux'
import ThemeInterface from '@/theme-interface'
import { removeChecksumCalculation } from '@/redux/ducks/document/upload'
import ProgressCircle from '@/modules/base/components/progress-indicators/progress-circle'
import customDecodeUri from '@/utils/customDecodeUri'
import { splitFilename } from '@/modules/_helpers/splitFilename'
import lang from '@/lang'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import StatusAction from '@/modules/layout/components/menuV2/upload-status/status-action'
import IconCancel from '@assets/images/icons/error2.svg'
import { useAppSelector } from '@/redux/store'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    display: 'flex',
    '&:hover': {
      background: theme.backgroundColor,
    },
    padding: 4,
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: '1 1 0',
    overflow: 'hidden',
    lineHeight: 1.3,
    marginTop: 3,
    marginRight: 10,
  },
  label: {
    maxWidth: '100%',
    overflow: 'hidden',
    color: theme.black,
    display: 'flex',
  },
  fileName: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  suffix: {
    marginRight: 10,
  },
  progress: {
    color: theme.grey1,
    margin: CssHelper.px(3, 8, 3, 5),
  },
  subTitle: {
    color: theme.grey1,
    flex: '1 1 0',
  },
  error: {
    color: theme.error,
    flex: '1 1 0',
  },
}))

interface CalculatingUploadProps {
  uuid: string,
}

const CalculatingUploadStatus = (props: CalculatingUploadProps) => {
  const dispatch = useDispatch()
  const theme: ThemeInterface = useTheme()
  const classes = useStyles()

  const { uuid } = props

  const calculating = useAppSelector((state) => state.document.upload.calculating).find((item) => uuid === item.uuid)
  const {
    fileName,
    currentChunk,
    totalChunks,
    error,
    translatedError,
    running,
  } = calculating

  const { name, suffix } = splitFilename(customDecodeUri(fileName))

  const color = error
    ? theme.error
    : currentChunk === totalChunks
      ? theme.green
      : running
        ? theme.grey2
        : theme.primary

  const onCancel = () => {
    dispatch(removeChecksumCalculation(uuid))
  }

  return (
    <div className={classes.content}>
      <ProgressCircle
        size={36}
        customClass={classes.progress}
        borderColor={color}
        borderWidth={2}
        fontSize={'11px'}
        progress={Math.round(((currentChunk ?? 0) / totalChunks) * 100)}
      />
      <span className={classes.header}>
        <span className={classes.label}>
          <span className={classes.fileName}>{name}</span>
          <span className={classes.suffix}>{suffix}</span>
        </span>
        {error
          ? <span className={classes.error}>{translatedError ?? lang.documents.calculation.error}</span>
          : <span className={classes.subTitle}>{lang.documents.calculation.label}</span>}
      </span>
      <StatusAction color={theme.black} onClick={onCancel} icon={IconCancel} tooltip={lang.documents.calculation.tooltips.cancel} />
    </div>
  )
}

export default CalculatingUploadStatus
